/**
 * The group the user was last looking at.
 *
 * The bottom navigation on a phone is built from the *current* group, but the
 * user spends plenty of time on routes that have no group in the URL — Home,
 * Admin, an invite link. Without a remembered value the bar loses half its
 * items the moment you leave a gallery, which reads as the app forgetting
 * where you were.
 *
 * So the bar follows the last group you actually opened, and only changes when
 * you deliberately pick a different one.
 */

const KEY = "fc-last-group";

let cached: string | null = null;
const listeners = new Set<(groupId: string | null) => void>();

export function getLastGroupId(): string | null {
  if (cached !== null) return cached;
  try {
    cached = localStorage.getItem(KEY);
  } catch {
    cached = null;
  }
  return cached;
}

export function setLastGroupId(groupId: string | null): void {
  if (cached === groupId) return;
  cached = groupId;

  try {
    if (groupId) localStorage.setItem(KEY, groupId);
    else localStorage.removeItem(KEY);
  } catch {
    // Private browsing with storage disabled: the in-memory value still works
    // for this session, which is the part that matters.
  }

  for (const listener of listeners) listener(groupId);
}

/** Subscribe so the nav re-renders when the remembered group changes. */
export function subscribeLastGroup(
  listener: (groupId: string | null) => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
